/**
 * Homeowner auth routes (OpenAPI tag: auth (homeowner)).
 * Base URL already includes `/api`; paths are relative to that root.
 */
export const AUTH_ENDPOINTS = {
    SIGNUP: '/auth/signup',
    /** @deprecated Password login removed for homeowners — use REQUEST_LOGIN_OTP */
    LOGIN: '/auth/login',
    REQUEST_LOGIN_OTP: '/auth/login/request-otp',
    GOOGLE: '/auth/google',
    APPLE: '/auth/apple',
    VERIFY_OTP: '/auth/verify-email',
    RESEND_OTP: '/auth/resend-verification',
    /** @deprecated Homeowner password reset removed */
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/me',
    UPDATE_PROFILE: '/auth/update-profile',
    CHANGE_PASSWORD: '/auth/password',
} as const;

/** Taxonomy / category reference data (authenticated homeowner). */
export const CATEGORY_ENDPOINTS = {
    CHILDREN_LIST_ALL: '/categories/children/list-all',
} as const;

export const VENDOR_ENDPOINTS = {
    CREATE: '/vendors/create',
    LIST: '/vendors/list',
    update: (id: string) => `/vendors/update/${encodeURIComponent(id)}`,
} as const;

export const FILE_ENDPOINTS = {
    UPLOAD: '/files/upload',
} as const;

export const DOCUMENT_ENDPOINTS = {
    BROWSE: '/documents/browse',
    SAVE: '/documents/save',
    delete: (documentId: string) => `/documents/delete/${encodeURIComponent(documentId)}`,
} as const;

export const ASSET_ENDPOINTS = {
    BROWSE: '/assets/browse',
    LIST: '/assets/list',
    CAPTURE_ANALYZE: '/assets/capture/analyze',
    CREATE: '/assets/create',
    documentsList: (assetId: string) => `/assets/${encodeURIComponent(assetId)}/documents/list`,
    documentsUpload: (assetId: string) => `/assets/${encodeURIComponent(assetId)}/documents/upload`,
    documentProposals: (assetId: string) =>
        `/assets/${encodeURIComponent(assetId)}/document-proposals`,

    get: (assetId: string) => `/assets/get/${encodeURIComponent(assetId)}`,
    update: (assetId: string) => `/assets/update/${encodeURIComponent(assetId)}`,
    delete: (assetId: string) => `/assets/delete/${encodeURIComponent(assetId)}`,
} as const;

export const DOCUMENT_PROPOSAL_ENDPOINTS = {
    apply: (id: string) => `/document-proposals/${encodeURIComponent(id)}/apply`,
    discard: (id: string) => `/document-proposals/${encodeURIComponent(id)}/discard`,
} as const;

export const FINDING_ENDPOINTS = {
    LIST: '/findings',
    CREATE: '/findings',
    confirm: (id: string) => `/findings/${encodeURIComponent(id)}/confirm`,
    dismiss: (id: string) => `/findings/${encodeURIComponent(id)}/dismiss`,
    resolve: (id: string) => `/findings/${encodeURIComponent(id)}/resolve`,
} as const;

export const SCORE_SNAPSHOT_ENDPOINTS = {
    LIST: '/score-snapshots',
} as const;

export const SERVICE_ENDPOINTS = {
    LIST: '/services/list',
    SUMMARY: '/services/summary',
    SCHEDULE: '/services/schedule',
    LOG_COMPLETED: '/services/log-completed',
    cancel: (serviceRecordId: string) =>
        `/services/cancel/${encodeURIComponent(serviceRecordId)}`,
    update: (serviceRecordId: string) =>
        `/services/update/${encodeURIComponent(serviceRecordId)}`,
} as const;

export const PROPERTY_ENDPOINTS = {
    LIST: '/properties/list',
    ACTIVATE: (propertyId: string) => `/properties/activate/${encodeURIComponent(propertyId)}`,
    DETAIL: (propertyId: string) => `/properties/get/${encodeURIComponent(propertyId)}/detail`,
    WIZARD_COMPLETE: '/properties/wizard/complete',
    WIZARD_UPDATE: (propertyId: string) => `/properties/wizard/update/${encodeURIComponent(propertyId)}`,
    FLOOR_PLAN_ANALYZE: '/properties/floor-plan/analyze',
    ROOM_PHOTO_ANALYZE: '/properties/room-photo/analyze',
    bulkCreateAssets: (propertyId: string) =>
        `/properties/${encodeURIComponent(propertyId)}/assets/bulk-create`,
    roomsList: (propertyId: string) => `/properties/${encodeURIComponent(propertyId)}/rooms/list`,
    delete: (propertyId: string) => `/properties/delete/${encodeURIComponent(propertyId)}`,
    images: (propertyId: string) => `/properties/${encodeURIComponent(propertyId)}/images`,
} as const;

/** OpenAPI tag: auth (homeowner) — homeowner subscription invoices, etc. */
export const SUBSCRIPTION_ENDPOINTS = {
    PLANS: '/subscriptions/plans',
    ME: '/subscriptions/me',
    START_TRIAL: '/subscriptions/start-trial',
    CHECKOUT: '/subscriptions/checkout',
    BILLING_PORTAL: '/subscriptions/billing-portal',
    CHANGE_PLAN: '/subscriptions/change-plan',
    CANCEL: '/subscriptions/cancel',
    INVOICES: '/subscriptions/invoices',
    invoiceDownload: (id: string) => `/subscriptions/invoices/${encodeURIComponent(id)}/download`,
} as const;

/** Served by the local BFF — not envella-api yet. */
export const BFF_OCR_ENDPOINTS = {
    PARSE: '/api/ocr/parse',
} as const;

/** Local Vite BFF — Google Places REST proxied with GOOGLE_PLACES_API_KEY. */
export const BFF_PLACES_ENDPOINTS = {
    AUTOCOMPLETE: '/api/places/autocomplete',
    DETAILS: '/api/places/details',
} as const;

/** RentCast property search (proxied by envella-api; API key stays server-side). */
export const RENTCAST_ENDPOINTS = {
    PROPERTIES: '/rentcast/properties',
} as const;

/** Public CMS legal pages (no login required). */
export const CMS_ENDPOINTS = {
    PAGES: '/cms/pages',
    page: (slug: string) => `/cms/pages/${encodeURIComponent(slug)}`,
} as const;
